/* eslint-disable */
import React, { useCallback } from 'react';
import compose from 'lodash/fp/compose';
import { useSelector } from 'react-redux';
import { useFavoriteOrganisation } from 'hooks/useFavoriteOrganisation';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';
import { useListCreditCard } from 'hooks/useListCreditCard';
import {
  withTranslation,
  withUserPreferences,
  withCustomRouter,
  withUserDefaultSpace,
  withPersonalInfo,
  withCurrency,
} from 'components/hoc';
import TransactionContentTemplate from '../../common/TransactionContentTemplate';
import { useListTransaction } from '../hooks/useListTransaction';
import { getFullName } from 'components/utils';
import { getTransactionItem, getDCIndicator } from '../utils';
import LottieLoader from 'components/common/LottieLoader';

const Income = (props) => {
  const {
    data,
    refreshTransactions,
    lastItemRef,
    loading,
    filters,
    setFilters,
    applyFilters,
  } = useListTransaction('income');
  const { fn, fd, userDefaultSpace, trObj, currencyLocaleOptions } = props;
  const favoriteOrganisation = useFavoriteOrganisation();
  const referenceCheckingAccount = useReferenceCheckingAccount();
  const creditCardOptions = useListCreditCard();
  const persons = useSelector((state) => state.personalInfo.data);

  // Add filterableColumns configuration
  const filterableColumns = [
    {
      columnId: 'person',
      columnDataType: 'text',
    },
    {
      columnId: 'description',
      columnDataType: 'text',
    },
    {
      columnId: 'date',
      columnDataType: 'date',
    },
    {
      columnId: 'partner',
      columnDataType: 'text',
    },
    {
      columnId: 'amount',
      columnDataType: 'number',
    },
    {
      columnId: 'amount_sc',
      columnDataType: 'number',
    },
    {
      columnId: 'currency',
      columnDataType: 'text',
    },
    {
      columnId: 'contract',
      columnDataType: 'text',
    },
    {
      columnId: 'dc_indicator',
      columnDataType: 'text',
    },
        {
      columnId: 'partner_org_ind',
      columnDataType: 'text',
    },
    {
      columnId: 'bpos_org_indicator',
      columnDataType: 'text',
    },
    {
      columnId: 'account_category',
      columnDataType: 'text',
    },
    {
      columnId: 'financial_account',
      columnDataType: 'text',
    },
    {
      columnId: 'offset_account_category',
      columnDataType: 'text',
    },
    {
      columnId: 'offset_account',
      columnDataType: 'text',
    },
    {
      columnId: 'reversal',
      columnDataType: 'text',
    },
    {
      columnId: 'reversed',
      columnDataType: 'text',
    },
    {
      columnId: 'reversal_doc',
      columnDataType: 'text',
    },
    {
      columnId: 'original_doc',
      columnDataType: 'text',
    },
    {
      columnId: 'year',
      columnDataType: 'number',
    },
    {
      columnId: 'period',
      columnDataType: 'text',
    },
    {
      columnId: 'measurement_unit',
      columnDataType: 'text',
    },
    {
      columnId: 'quantity',
      columnDataType: 'number',
    },
    {
      columnId: 'line',
      columnDataType: 'number',
    },
    {
      columnId: 'position_link',
      columnDataType: 'text',
    },
    {
      columnId: 'sp_currency',
      columnDataType: 'text',
    },
    {
      columnId: 'transaction',
      columnDataType: 'number',
    },
    {
      columnId: 'reverse_doc_no',
      columnDataType: 'number',
    },
    {
      columnId: 'original_doc_no',
      columnDataType: 'number',
    },
  ];

  const getPositionLinkName = useCallback(
    (transactionItem) => {
      const { position_link, financial_account, amount, currency } = transactionItem;

      // Create lookup maps
      const personMap = new Map(
        persons.map((p) => [Number(p.id), `${p.first_name} ${p.last_name}`]),
      );
      const orgMap = new Map(
        favoriteOrganisation.map((o) => [String(o.id), o.name]),
      );
      const accountMap = new Map(
        referenceCheckingAccount.map((a) => [a.id, a.account_no]),
      );
      const creditCardMap = new Map(
        creditCardOptions.map((c) => [c.id, `${c.number}`]),
      );

      if (['receive', 'payable'].includes(financial_account)) {
        return (
          personMap.get(Number(position_link)) ||
          orgMap.get(String(position_link)) ||
          position_link
        );
      }
      if (financial_account === 'bank') {
        return accountMap.get(position_link) || position_link;
      }

      if (financial_account === 'cash') {
        const matchedOption = currencyLocaleOptions.find(
          (currency) => currency.id === position_link,
        );
        return matchedOption ? trObj(matchedOption) : position_link;
      }

      if (financial_account === 'credit') {
        // Show the credit card number (not the id)
        return (
          creditCardMap.get(Number(position_link)) ||
          creditCardMap.get(String(position_link)) ||
          position_link
        );
      }
      return position_link;
    },
    [persons, favoriteOrganisation, referenceCheckingAccount, creditCardOptions],
  );

  const mapFields = () => {
    return data?.map((income) => {
      const transactionItem = getTransactionItem(income.transaction_item[0], fn);
      const linkedTransactionItem = income.transaction_item[0] || {};
      
      // Find the transaction item that has person data
      const personTransactionItem = income.transaction_item.find(item => item.person_details) || income.transaction_item[0];
 
      return {
        ...income,
        ...transactionItem,
        space_name: income.space,
        // Use the correct transaction item for person data
        person: getFullName(personTransactionItem?.person_details || income.person_detail),
        description: income.description,
        date: fd(income.date),
        partner: income?.partner_detail?.name,
        // Use the correct transaction item index for amount data
        amount: fn(personTransactionItem?.amount || income.transaction_item[0]?.amount),
        amount_sc: fn(personTransactionItem?.amount_sc || income.transaction_item[0]?.amount_sc),
        sp_currency: userDefaultSpace.space_detail.currency,
        currency: transactionItem.currency,
        contract: income.contract_details?.description,
        dc_indicator: getDCIndicator(transactionItem.dc_indicator),
        _partner_org_ind: income.partner_org_ind,  

        account_category: props.trObj(income.transaction_item[1]?.account_category_details),
        financial_account: props.trObj(income.transaction_item[1]?.financial_account_details),
        offset_account_category: props.trObj(
          income.transaction_item[0]?.account_category_details,
        ),
        offset_account: props.trObj(
          income.transaction_item[0]?.financial_account_details,
        ),
        reversal: income.reversal ? '\u2713' : '\u2717',
        reversed: income.reversed ? '\u2713' : '\u2717',
        measurement_unit: trObj(transactionItem.measurement_unit_detail),
        reverse_doc_no: income.reverse_doc_no,
        original_doc_no: income.original_doc_no,
        position_link: getPositionLinkName(linkedTransactionItem),
      };
    });
  };

  const { gotoCreatePath } = props;

  const tableProps = {
    frontendTableName: 'income',
    backendTableName: 'transaction',
  };

  if (loading)
    return (
      <>
        <LottieLoader />
      </>
    );

  return (
    <TransactionContentTemplate
      {...tableProps}
      columns={[]}
      data={mapFields() || []}
      toggleForm={gotoCreatePath}
      hasBulkDelete={false}
      refreshTransactions={refreshTransactions}
      filterableColumns={filterableColumns}
      filters={filters}
      setFilters={setFilters}
      applyFilters={applyFilters}
      lastItemRef={lastItemRef}
    />
  );
};

export default compose(
  withTranslation,
  withUserPreferences,
  withCustomRouter,
  withUserDefaultSpace,
  withPersonalInfo,
  withCurrency,
)(Income);
